import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

import {
	getPolicyFromTransactionObj,
	getCommissionRatio,
	getCommissionAmount,
	getCommissionEntityName,
	getTransactionsFact,
	getBinderNumber,
	formatDate
} from "./helpers"

const header = [
	[
		"Debit/Invoice No.",
		"Policy No.",
		"Invoice Date",
		"Installment No",
		"Installment %",
		"Customer No",
		"Customer Name",
		"Order Ref No",
		"Currency",
		"Gross Receivable",
		"Total Discount",
		"Underwriter's A/C No.",
		"Broker/Salesman",
		"Broker/Salesman Comm.",
		"Net Receivable",
		"Gross Payable",
		"Agency",
		"Agency Commission",
		"Net Payable",
		"Net Comm. Receivable"
	]
]

/****************** Helper Functions ******************/
const getTotalAppliedDiscounts = transaction => {
	const policy = getPolicyFromTransactionObj(transaction)
	const sum = policy?.premium?.appliedDiscounts?.reduce((sum, item) => {
		let flat = item?.flat ? item?.flat : 0
		return sum + flat
	}, 0)
	return sum ? sum : 0
}

const getBrokerSalesmanCommission = transaction => {
	const policy = getPolicyFromTransactionObj(transaction)
	return (
		getCommissionAmount("broker", transaction?.amount, policy) ||
		getCommissionAmount("salesman", transaction?.amount, policy) ||
		0
	)
}

const getBrokerSalesmanName = transaction => {
	const policy = getPolicyFromTransactionObj(transaction)
	return (
		getCommissionEntityName("broker", policy) ||
		getCommissionEntityName("salesman", policy)
	)
}

const getAgencyCommission = transaction => {
	const policy = getPolicyFromTransactionObj(transaction)

	return (
		getCommissionAmount("agency", transaction?.amount, policy) ||
		getCommissionAmount("thirdParty", transaction?.amount, policy) ||
		0
	)
}

const getAgencyName = transaction => {
	const policy = getPolicyFromTransactionObj(transaction)
	return (
		getCommissionEntityName("agency", policy) ||
		getCommissionEntityName("thirdParty", policy)
	)
}

const getNetcommission = transaction => {
	const policy = getPolicyFromTransactionObj(transaction)
	let result = getCommissionAmount("coverholder", transaction?.amount, policy)
	return isNaN(result) ? 0 : result
}

const getBreakdownTotal = transactions => {
	let output = [
		[
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"Total no. of orders",
			"Currency",
			"Gross Receivable",
			"Total Discount",
			"Broker / Salesman Comm.",
			"Net Receievable",
			"Gross Payable",
			"Agency Comm.",
			"Net Payable",
			"Net Comm. Receivable"
		]
	]

	// group data by currencyCode
	let groupedData = {}
	transactions.map(transaction => {
		const currencyCode = transaction?.currencyCode
			? transaction?.currencyCode
			: ""

		if (!Object.keys(groupedData).includes(currencyCode)) {
			groupedData[currencyCode] = []
		}
		groupedData[currencyCode].push(transaction)
	})

	Object.values(groupedData)?.map((group, index) => {
		const currencyCode = group?.[0]?.currencyCode
		const totalGrossReceivable = group?.reduce((sum, transaction) => {
			return sum + transaction?.amount
		}, 0)
		const totalDiscount = group?.reduce((sum, transaction) => {
			const value = getTotalAppliedDiscounts(transaction)
			return sum + value
		}, 0)
		const totalBrokerSalesmanCommission = group?.reduce((sum, transaction) => {
			const value = getBrokerSalesmanCommission(transaction)
			return sum + value
		}, 0)
		const totalNetReceivable = group?.reduce((sum, transaction) => {
			const value = transaction?.amount - getTotalAppliedDiscounts(transaction)
			return sum + value
		}, 0)
		const totalGrossPayable = group?.reduce((sum, transaction) => {
			const value = transaction?.amount //TODO: normally this is grossReceivable - rebate (rebate in certain products, but none of products have rebate right now)
			return sum + value
		}, 0)
		const totalAgencyCommission = group?.reduce((sum, transaction) => {
			const value = getAgencyCommission(transaction)
			return sum + value
		}, 0)
		const totalNetPayable = group?.reduce((sum, transaction) => {
			const value =
				transaction?.amount -
				getTotalAppliedDiscounts(transaction) -
				getNetcommission(transaction)
			return sum + value
		}, 0)
		const totalNetCommission = group?.reduce((sum, transaction) => {
			const value = getNetcommission(transaction)
			return sum + value
		}, 0)
		output.push([
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			group?.length,
			currencyCode,
			totalGrossReceivable,
			totalDiscount,
			totalBrokerSalesmanCommission,
			totalNetReceivable,
			totalGrossPayable,
			totalAgencyCommission,
			totalNetPayable,
			totalNetCommission
		])
	})
	return output
}

/****************** Define Rows ******************/
const rows = fetchedData => {
	let output = []

	// transaction per row
	fetchedData?.reverse()?.map(transaction => {
		const policy = getPolicyFromTransactionObj(transaction)
		const netReceivable =
			transaction?.amount - getTotalAppliedDiscounts(transaction)
		const netPayable =
			netReceivable -
			getAgencyCommission(transaction) -
			getNetcommission(transaction) -
			getBrokerSalesmanCommission(transaction)

		output.push([
			transaction?.transactionNumber,
			policy?.issuerNumber,
			formatDate(transaction?.dateTime),
			"",
			"",
			"", //TODO: We do not have customer number until AEG ERP is fully migrated to CRM
			policy?.contractHolder?.name,
			getTransactionsFact("orderReferenceNumber", "stringValue", transaction),
			transaction?.currencyCode,
			transaction?.amount,
			getTotalAppliedDiscounts(transaction),
			getBinderNumber(getPolicyFromTransactionObj(transaction)),
			getBrokerSalesmanName(transaction),
			getBrokerSalesmanCommission(transaction),
			netReceivable,
			transaction?.amount, //TODO: normally this is grossReceivable - rebate (rebate in certain products, but none of products have rebate right now)
			getAgencyName(transaction),
			getAgencyCommission(transaction),
			netPayable,
			getNetcommission(transaction)
		])
	})

	output.push([], [], ...getBreakdownTotal(fetchedData), [])

	return output
}

/****************** Function for fetching ******************/
const fetchTransactions = async filterPayload => {
	const dateFrom = filterPayload?.issueDate?.after
		? dayjs(filterPayload?.issueDate?.after).format()
		: null
	const dateTo = filterPayload?.issueDate?.before
		? dayjs(filterPayload?.issueDate?.before)
				.add(1, "day")
				.format()
		: null

	const query = gql`
		fragment obj on policy {
			issuerNumber
			contractHolder {
				name
			}
			premium {
				currencyCode
				appliedDiscounts {
					flat
				}
			}
			product {
				productId {
					type
				}
			}
			stakeholders {
				entity {
					internalCode
				}
				type
			}
			commissionSplitRules {
				type
				currencyCode
				amount
				remark
				ratio
				entity {
					name
				}
			}
			associatedContracts {
				contract {
					facts {
						type
						value {
							stringValue
						}
					}
				}
			}
		}
		query fetchTransactions($transactionWhere: transactionWhereInput) {
			transactions(where: $transactionWhere) {
				list {
					currencyCode
					transactionNumber
					dateTime
					amount
					facts {
						type
						value {
							stringValue
						}
					}
					object {
						__typename
						... on policy {
							...obj
							endorsements {
								beforeEndorsement {
									...obj
								}
							}
						}
						... on endorsement {
							afterEndorsement {
								...obj
							}
						}
					}
				}
			}
		}
	`
	const variables = {
		transactionWhere: {
			and: [
				{ dateTime_gt: dateFrom },
				{ dateTime_lt: dateTo },
				{ status: "APPROVED" }
			]
		}
	}

	const res = await api({ query, variables })
	//TODO: error handling
	return res?.data?.transactions?.list
}

/****************** Export ******************/
export default async filterPayload => {
	const fetchedData = await fetchTransactions(filterPayload)
	const workSheetData = [...header, ...rows(fetchedData)]
	const workSheetColumns = header?.[0]?.map(item => ({ wpx: 200 }))
	return { workSheetData, workSheetColumns }
}
